'use client';
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Spinner, Alert, Button, ButtonGroup } from 'react-bootstrap';
import { GitCompare, Package, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ProductTableHeader } from './components/ProductTableHeader';
import { CompetitorProductFilters, MatchTypeFilter } from './components/CompetitorProductFilters';
import { ProductCard } from './components/ProductCard';
import CompetitorSelectionSection from './components/CompetitorSelectionSection';
import {
  CompetitorClient,
  MatchedSkinSeoulProduct,
  FetchExclusiveProductsParams,
  FetchMatchedProductsParams,
} from '../../../infrastructure/CompetitorClient';
import type {
  ProductData,
  PaginationState,
  AlertFilter,
  AlertInfo,
  CandidateFilter,
  SortState,
  SortField,
} from './models/types';
import {
  detectAlerts,
  hasCompetitorCandidates,
  reviewersOf,
  selectMatchesInUse,
  selectNonVariationProducts,
  skuEndsWithDigit,
  sortProducts,
} from './models/types';
import { useAuth } from '../../../providers/AuthProvider';

export default function MatchSKUPage() {
  const competitorClient = new CompetitorClient();
  const { user } = useAuth();
  const reviewerEmail = user?.email;
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedProduct, setSelectedProduct] = useState<ProductData | null>(null);
  const [activeCompetitor, setActiveCompetitor] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Record<string, Record<string, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductData[]>([]);

  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
  const [searchPage, setSearchPage] = useState(1);
  const [matchType, setMatchType] = useState<MatchTypeFilter>('matched');
  const [alertFilter, setAlertFilter] = useState<AlertFilter>('all');
  const [candidateFilter, setCandidateFilter] = useState<CandidateFilter>('all');
  const [skuLastDigit, setSkuLastDigit] = useState<string>('');
  const [actionedByFilter, setActionedByFilter] = useState<string>('');
  const [knownReviewers, setKnownReviewers] = useState<string[]>([]);
  const [sort, setSort] = useState<SortState>({ field: 'modifiedAt', direction: 'desc' });

  const [pagination, setPagination] = useState<PaginationState>({ limit: 50, lastEvaluatedKey: null });
  const [machineUnmatchedPagination, setMachineUnmatchedPagination] = useState<{ offset: number; total: number | null }>({ offset: 0, total: null });
  const [humanUnmatchedPagination, setHumanUnmatchedPagination] = useState<{ lastEvaluatedKey: string | null }>({ lastEvaluatedKey: null });
  const [exclusivePagination, setExclusivePagination] = useState<{ lastEvaluatedKey: string | null }>({
    lastEvaluatedKey: null,
  });
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [isPaginationStopped, setIsPaginationStopped] = useState(false);
  const [displayPage, setDisplayPage] = useState(1);
  const itemsPerPage = 50;
  const fetchLimit = 100;

  const [leftWidth, setLeftWidth] = useState(50);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());

  const toggleProductExpansion = (sku: string) => {
    setExpandedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sku)) {
        newSet.delete(sku);
      } else {
        newSet.add(sku);
      }
      return newSet;
    });
  };

  const isDirectSkuSearch = !!appliedSearchQuery && /^\d+$/.test(appliedSearchQuery);
  const isEmptySearch = !appliedSearchQuery;

  const listMatchedProductsQuery = useQuery({
    queryKey: ['list-matched-products', pagination.limit, pagination.lastEvaluatedKey, actionedByFilter],
    queryFn: async () => {
      const params: FetchMatchedProductsParams = { limit: pagination.limit };
      if (pagination.lastEvaluatedKey) params.lastEvaluatedKey = pagination.lastEvaluatedKey;
      if (actionedByFilter) {
        params.actionedBy = actionedByFilter;
      }
      const result = await competitorClient.fetchPaginatedMatchedProducts(params);
      return result;
    },
    enabled: isSearchEnabled && isEmptySearch && matchType === 'matched',
    staleTime: 60000,
  });

  const exclusiveProductsQuery = useQuery({
    queryKey: ['exclusive-products', pagination.limit, exclusivePagination.lastEvaluatedKey, appliedSearchQuery],
    queryFn: () => {
      const params: FetchExclusiveProductsParams = { limit: pagination.limit };
      if (exclusivePagination.lastEvaluatedKey) params.lastEvaluatedKey = exclusivePagination.lastEvaluatedKey;
      if (appliedSearchQuery) params.search = appliedSearchQuery;
      return competitorClient.fetchExclusiveProducts(params);
    },
    enabled: isSearchEnabled && matchType === 'exclusive',
    staleTime: 60000,
  });

  const machineUnmatchedQuery = useQuery({
    queryKey: [
      'machine-unmatched-products',
      machineUnmatchedPagination.offset,
      fetchLimit,
      appliedSearchQuery,
      skuLastDigit,
    ],
    queryFn: () =>
      competitorClient.fetchMachineUnmatchedProducts({
        limit: fetchLimit,
        offset: machineUnmatchedPagination.offset,
        ...(appliedSearchQuery ? { search: appliedSearchQuery } : {}),
        ...(skuLastDigit ? { skuLastDigit } : {}),
      }),
    enabled: isSearchEnabled && matchType === 'machineUnmatched',
    staleTime: 60000,
  });

  const humanUnmatchedQuery = useQuery({
    queryKey: [
      'human-unmatched-products',
      humanUnmatchedPagination.lastEvaluatedKey,
      fetchLimit,
      appliedSearchQuery,
      candidateFilter,
      skuLastDigit,
    ],
    queryFn: () => {
      const params: {
        limit: number;
        lastEvaluatedKey?: string;
        search?: string;
        hasCandidates?: boolean;
        skuLastDigit?: string;
      } = { limit: fetchLimit };
      if (humanUnmatchedPagination.lastEvaluatedKey) {
        params.lastEvaluatedKey = humanUnmatchedPagination.lastEvaluatedKey;
      }
      if (appliedSearchQuery) {
        params.search = appliedSearchQuery;
      }
      if (candidateFilter !== 'all') {
        params.hasCandidates = candidateFilter === 'with_candidates';
      }
      if (skuLastDigit) {
        params.skuLastDigit = skuLastDigit;
      }
      return competitorClient.fetchMachineMatchedProducts(params);
    },
    enabled: isSearchEnabled && matchType === 'humanUnmatched',
    staleTime: 60000,
  });

  const searchProductsQuery = useQuery({
    queryKey: ['search-products', appliedSearchQuery, searchPage, pagination.limit],
    queryFn: async () => {
      const searchResult = await competitorClient.searchProducts({
        q: appliedSearchQuery,
        page: searchPage,
        limit: pagination.limit,
      });
      return searchResult;
    },
    enabled: isSearchEnabled && !isDirectSkuSearch && !isEmptySearch && matchType === 'matched',
    staleTime: 60000,
  });

  const skus = isDirectSkuSearch
    ? [appliedSearchQuery]
    : searchProductsQuery.data?.products?.map((p) => p.sku) || [];

  const matchedProductsQuery = useQuery({
    queryKey: ['matched-products-by-skus', skus],
    queryFn: async () => {
      if (skus.length === 0) return [];
      const matchedProducts = await competitorClient.getMatchedProductsBySkus(skus);
      return matchedProducts;
    },
    enabled: skus.length > 0 && (isDirectSkuSearch || searchProductsQuery.isSuccess) && matchType === 'matched',
    staleTime: 60000,
  });

  useEffect(() => {
    if (matchType === 'machineUnmatched' && machineUnmatchedQuery.data) {
      const response = machineUnmatchedQuery.data;
      const converted = response.items
        .filter((item: any) => item.sku)
        .map((item: any) => ({
          skinSeoulProduct: { ...item, size: item.size || '', pack: item.pack || '' },
          machineMatchedProducts: [],
          humanMatchedProducts: [],
        })) as ProductData[];

      setProducts((prev) => {
        const existingSkus = new Set(prev.map((p) => p.skinSeoulProduct.sku));
        const newProducts = converted.filter((p) => !existingSkus.has(p.skinSeoulProduct.sku));
        return [...prev, ...newProducts];
      });

      if (response.total !== undefined) {
        setMachineUnmatchedPagination((prev) => ({ ...prev, total: response.total }));
      }
      if (!isInitialLoadDone) setIsInitialLoadDone(true);
    } else if (matchType === 'humanUnmatched' && humanUnmatchedQuery.data) {
      const response = humanUnmatchedQuery.data;
      const converted = response.items
        .filter((item: any) => item.skinSeoulProduct)
        .map((item: any) => {
          const sp = item.skinSeoulProduct as Record<string, unknown>;
          return {
            skinSeoulProduct: { ...item.skinSeoulProduct, size: (sp['size'] as string) || '', pack: (sp['pack'] as string) || '' },
            machineMatchedProducts: item.machineMatchedProducts || [],
            humanMatchedProducts: [],
            humanVerifiedNoCompetitor: item.humanVerifiedNoCompetitor,
          };
        }) as ProductData[];

      setProducts((prev) => {
        const existingSkus = new Set(prev.map((p) => p.skinSeoulProduct.sku));
        const newProducts = converted.filter((p) => !existingSkus.has(p.skinSeoulProduct.sku));
        return [...prev, ...newProducts];
      });

      if (!isInitialLoadDone) setIsInitialLoadDone(true);
    } else if (matchType === 'exclusive' && exclusiveProductsQuery.data) {
      const converted = exclusiveProductsQuery.data.items
        .filter((item: any) => item.skinSeoulProduct)
        .map((item: any) => {
          const sp = item.skinSeoulProduct as Record<string, unknown>;
          return {
            skinSeoulProduct: { ...item.skinSeoulProduct, size: (sp['size'] as string) || '', pack: (sp['pack'] as string) || '' },
            machineMatchedProducts: item.machineMatchedProducts || [],
            humanMatchedProducts: item.humanMatchedProducts || [],
            humanVerifiedNoCompetitor: item.humanVerifiedNoCompetitor,
            humanDecidedBy: item.humanDecidedBy,
            humanDecidedAt: item.humanDecidedAt,
          };
        }) as ProductData[];

      setProducts((prev) => {
        const existingSkus = new Set(prev.map((p) => p.skinSeoulProduct.sku));
        const newProducts = converted.filter((p) => !existingSkus.has(p.skinSeoulProduct.sku));
        return [...prev, ...newProducts];
      });

      if (!isInitialLoadDone) setIsInitialLoadDone(true);
    } else if (matchType === 'matched' && isEmptySearch && listMatchedProductsQuery.data) {
      const converted = listMatchedProductsQuery.data.items
        .filter((item: any) => item.skinSeoulProduct)
        .map((item: any) => {
          const sp = item.skinSeoulProduct as Record<string, unknown>;
          return {
            skinSeoulProduct: { ...item.skinSeoulProduct, size: (sp['size'] as string) || '', pack: (sp['pack'] as string) || '' },
            machineMatchedProducts: item.machineMatchedProducts || [],
            humanMatchedProducts: item.humanMatchedProducts || [],
            humanVerifiedNoCompetitor: item.humanVerifiedNoCompetitor,
            humanDecidedBy: item.humanDecidedBy,
            humanDecidedAt: item.humanDecidedAt,
          };
        }) as ProductData[];

      setProducts((prev) => {
        const existingSkus = new Set(prev.map((p) => p.skinSeoulProduct.sku));
        const newProducts = converted.filter((p) => !existingSkus.has(p.skinSeoulProduct.sku));
        return [...prev, ...newProducts];
      });

      if (!isInitialLoadDone) setIsInitialLoadDone(true);
    } else if (matchType === 'matched' && matchedProductsQuery.data) {
      const converted = matchedProductsQuery.data
        .filter((mp: MatchedSkinSeoulProduct) => mp?.skinSeoulProduct)
        .map((mp: MatchedSkinSeoulProduct) => {
          const sp = mp.skinSeoulProduct as Record<string, unknown>;
          return {
            skinSeoulProduct: { ...mp.skinSeoulProduct, size: (sp['size'] as string) || '', pack: (sp['pack'] as string) || '' },
            machineMatchedProducts: mp.machineMatchedProducts || [],
            humanMatchedProducts: mp.humanMatchedProducts || [],
            humanVerifiedNoCompetitor: (mp as unknown as { humanVerifiedNoCompetitor?: boolean }).humanVerifiedNoCompetitor,
            humanDecidedBy: mp.humanDecidedBy,
            humanDecidedAt: mp.humanDecidedAt,
          };
        }) as ProductData[];
      setProducts(converted);
    }
  }, [matchType, isEmptySearch, listMatchedProductsQuery.data, matchedProductsQuery.data, machineUnmatchedQuery.data, humanUnmatchedQuery.data, exclusiveProductsQuery.data]);

  useEffect(() => {
    if (matchType !== 'matched' || !isEmptySearch || !isInitialLoadDone || listMatchedProductsQuery.isFetching) return;
    if (isPaginationStopped) {
      setIsBackgroundLoading(false);
      return;
    }
    const nextKey = listMatchedProductsQuery.data?.lastEvaluatedKey;
    if (nextKey) {
      setIsBackgroundLoading(true);
      setPagination((prev) => ({ ...prev, lastEvaluatedKey: nextKey }));
    } else {
      setIsBackgroundLoading(false);
    }
  }, [matchType, isEmptySearch, isInitialLoadDone, listMatchedProductsQuery.isFetching, listMatchedProductsQuery.data?.lastEvaluatedKey, isPaginationStopped]);

  useEffect(() => {
    if (matchType !== 'machineUnmatched' || !isInitialLoadDone || machineUnmatchedQuery.isFetching) return;
    if (isPaginationStopped) {
      setIsBackgroundLoading(false);
      return;
    }
    const total = machineUnmatchedPagination.total;
    const currentOffset = machineUnmatchedPagination.offset;
    if (total !== null && currentOffset + fetchLimit < total) {
      setIsBackgroundLoading(true);
      setMachineUnmatchedPagination((prev) => ({ ...prev, offset: currentOffset + fetchLimit }));
    } else {
      setIsBackgroundLoading(false);
    }
  }, [matchType, isInitialLoadDone, machineUnmatchedQuery.isFetching, machineUnmatchedPagination.total, isPaginationStopped]);

  useEffect(() => {
    if (matchType !== 'humanUnmatched' || !isInitialLoadDone || humanUnmatchedQuery.isFetching) return;
    if (isPaginationStopped) {
      setIsBackgroundLoading(false);
      return;
    }
    const nextKey = humanUnmatchedQuery.data?.lastEvaluatedKey;
    if (nextKey) {
      setIsBackgroundLoading(true);
      setHumanUnmatchedPagination({ lastEvaluatedKey: nextKey });
    } else {
      setIsBackgroundLoading(false);
    }
  }, [matchType, isInitialLoadDone, humanUnmatchedQuery.isFetching, humanUnmatchedQuery.data?.lastEvaluatedKey, isPaginationStopped]);

  useEffect(() => {
    if (matchType !== 'exclusive' || !isInitialLoadDone || exclusiveProductsQuery.isFetching) return;
    if (isPaginationStopped) {
      setIsBackgroundLoading(false);
      return;
    }
    const nextKey = exclusiveProductsQuery.data?.lastEvaluatedKey;
    if (nextKey) {
      setIsBackgroundLoading(true);
      setExclusivePagination({ lastEvaluatedKey: nextKey });
    } else {
      setIsBackgroundLoading(false);
    }
  }, [
    matchType,
    isInitialLoadDone,
    exclusiveProductsQuery.isFetching,
    exclusiveProductsQuery.data?.lastEvaluatedKey,
    isPaginationStopped,
  ]);

  const isLoading = products.length === 0 && (
    matchType === 'machineUnmatched'
      ? machineUnmatchedQuery.isLoading
      : matchType === 'humanUnmatched'
        ? humanUnmatchedQuery.isLoading
        : matchType === 'exclusive'
          ? exclusiveProductsQuery.isLoading
          : isEmptySearch
            ? listMatchedProductsQuery.isLoading
            : isDirectSkuSearch
              ? matchedProductsQuery.isLoading
              : searchProductsQuery.isLoading || matchedProductsQuery.isLoading
  );
  const executeSearch = () => {
    // Re-running the same search keeps the cached result, so clearing the list here would empty it for good.
    const isRepeatOfCurrentSearch = isSearchEnabled && searchQuery === appliedSearchQuery && products.length > 0;

    setAppliedSearchQuery(searchQuery);
    setIsSearchEnabled(true);
    setSearchPage(1);
    setIsBackgroundLoading(false);
    setIsPaginationStopped(false);
    setDisplayPage(1);

    if (!isRepeatOfCurrentSearch) {
      setProducts([]);
      setIsInitialLoadDone(false);
      setPagination({ limit: pagination.limit, lastEvaluatedKey: null });
      setMachineUnmatchedPagination({ offset: 0, total: null });
      setHumanUnmatchedPagination({ lastEvaluatedKey: null });
      setExclusivePagination({ lastEvaluatedKey: null });
    }

    const params: Record<string, string> = {};
    if (searchQuery) params['search'] = searchQuery;
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setAppliedSearchQuery('');
    setProducts([]);
    setSearchParams({});
    setSearchPage(1);
    setActionedByFilter('');
    setIsSearchEnabled(false);
    setIsInitialLoadDone(false);
    setIsBackgroundLoading(false);
    setIsPaginationStopped(false);
    setDisplayPage(1);
    setPagination({ limit: pagination.limit, lastEvaluatedKey: null });
    setMachineUnmatchedPagination({ offset: 0, total: null });
    setHumanUnmatchedPagination({ lastEvaluatedKey: null });
    setExclusivePagination({ lastEvaluatedKey: null });
  };

  const handleMatchTypeChange = (newMatchType: MatchTypeFilter) => {
    setMatchType(newMatchType);
    setActionedByFilter('');
    setKnownReviewers([]);
    setProducts([]);
    setIsInitialLoadDone(false);
    setIsBackgroundLoading(false);
    setIsPaginationStopped(false);
    setDisplayPage(1);
    setPagination({ limit: pagination.limit, lastEvaluatedKey: null });
    setMachineUnmatchedPagination({ offset: 0, total: null });
    setHumanUnmatchedPagination({ lastEvaluatedKey: null });
    setExclusivePagination({ lastEvaluatedKey: null });
    // machineUnmatched, humanUnmatched and exclusive load their list directly, without a search
    if (newMatchType === 'machineUnmatched' || newMatchType === 'humanUnmatched' || newMatchType === 'exclusive') {
      setIsSearchEnabled(true);
    } else {
      setIsSearchEnabled(false);
    }
  };

  const handlePageSizeChange = (limit: number) => {
    setProducts([]);
    setPagination({ limit, lastEvaluatedKey: null });
    setSearchPage(1);
    setIsInitialLoadDone(false);
    setIsBackgroundLoading(false);
    setIsPaginationStopped(false);
    setDisplayPage(1);
  };

  // Sorting is applied to the loaded list, so it must not discard what has already been fetched.
  const handleSortChange = (field: SortField) => {
    setSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setDisplayPage(1);
  };

  const handleCandidateFilterChange = (nextCandidateFilter: CandidateFilter) => {
    setCandidateFilter(nextCandidateFilter);
    resetQueueLists();
  };

  const handleSkuLastDigitChange = (nextLastDigit: string) => {
    setSkuLastDigit(nextLastDigit);
    resetQueueLists();
  };

  const handleActionedByChange = (nextActionedBy: string) => {
    setActionedByFilter(nextActionedBy);
    resetQueueLists();
  };

  const nonVariationProducts = useMemo(() => selectNonVariationProducts(products), [products]);

  // Filtering narrows the list to one reviewer, so the options are remembered rather than
  // recomputed from what is currently on screen. Variations are excluded because they never
  // become cards, so offering a reviewer known only from one would filter the queue to nothing.
  useEffect(() => {
    const loadedReviewers = reviewersOf(nonVariationProducts);
    if (loadedReviewers.length === 0) {
      return;
    }

    setKnownReviewers((previousReviewers) => {
      const mergedReviewers = [...new Set([...previousReviewers, ...loadedReviewers])].sort();
      return mergedReviewers.length === previousReviewers.length ? previousReviewers : mergedReviewers;
    });
  }, [nonVariationProducts]);

  const resetQueueLists = () => {
    setProducts([]);
    setIsInitialLoadDone(false);
    setIsBackgroundLoading(false);
    setIsPaginationStopped(false);
    setDisplayPage(1);
    setPagination({ limit: pagination.limit, lastEvaluatedKey: null });
    setMachineUnmatchedPagination({ offset: 0, total: null });
    setHumanUnmatchedPagination({ lastEvaluatedKey: null });
    setExclusivePagination({ lastEvaluatedKey: null });
  };

  const handleProductSelect = (product: ProductData) => {
    setSelectedProduct(product);
    const firstCompetitor = product.machineMatchedProducts.find((c) => c.competitorProducts.length > 0);
    setActiveCompetitor(firstCompetitor?.competitor || null);
    setError(null);
  };

  const countCompetitors = (product: ProductData): number => {
    const competitorSet = new Set<string>();
    product.machineMatchedProducts
      .filter((c) => c.competitorProducts.length > 0)
      .forEach((c) => competitorSet.add(c.competitor));
    product.humanMatchedProducts
      ?.filter((c) => c.competitorProducts.length > 0)
      .forEach((c) => competitorSet.add(c.competitor));
    return competitorSet.size;
  };

  const getProductAlerts = (product: ProductData): AlertInfo[] => {
    return selectMatchesInUse(product).flatMap((comp) =>
      comp.competitorProducts.flatMap((cp) => detectAlerts(product.skinSeoulProduct, cp as any)),
    );
  };

  const filterProductsByAlert = (productList: ProductData[]): ProductData[] => {
    if (alertFilter === 'all') return productList;

    return productList.filter((product) => {
      const alerts = getProductAlerts(product);
      if (alertFilter === 'no_alerts') return alerts.length === 0;
      return alerts.some((a) => a.type === alertFilter);
    });
  };

  // Each queue filter applies only where its control is offered, so a hidden filter can never narrow a list silently.
  const activeCandidateFilter = matchType === 'humanUnmatched' ? candidateFilter : 'all';
  const activeSkuLastDigit = matchType === 'matched' ? '' : skuLastDigit;
  const activeActionedBy = matchType === 'matched' ? actionedByFilter : '';
  const queueFilteredProducts = nonVariationProducts.filter((product) => {
    if (
      activeCandidateFilter !== 'all' &&
      hasCompetitorCandidates(product) !== (activeCandidateFilter === 'with_candidates')
    ) {
      return false;
    }
    // The listing endpoint already narrows to one reviewer, but a SKU search takes a different
    // path, and the dropdown stays on screen there. Without this the control would sit visibly
    // set while the results ignored it.
    if (activeActionedBy && product.humanDecidedBy !== activeActionedBy) {
      return false;
    }
    return skuEndsWithDigit(product.skinSeoulProduct.sku, activeSkuLastDigit);
  });
  const filteredProducts = sortProducts(filterProductsByAlert(queueFilteredProducts), sort);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const displayedProducts = filteredProducts.slice((displayPage - 1) * itemsPerPage, displayPage * itemsPerPage);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setDisplayPage(page);
  };

  const isProductSelected = (skinSeoulSku: string, competitor: string, competitorSku: string): boolean =>
    Boolean(selectedProducts[skinSeoulSku]?.[competitor] === competitorSku);

  const handleCompetitorProductSelect = (product?: { sku: string }) => {
    if (!selectedProduct || !activeCompetitor) return;
    const sku = selectedProduct.skinSeoulProduct.sku;
    setSelectedProducts((prev) => ({
      ...prev,
      [sku]: { ...prev[sku], [activeCompetitor]: product?.sku || '' },
    }));
  };

  const updateSelectedProduct = (updatedProduct: ProductData) => {
    if (selectedProduct?.skinSeoulProduct.sku === updatedProduct.skinSeoulProduct.sku) {
      setSelectedProduct(updatedProduct);
    }
  };

  const queryClient = useQueryClient();

  const belongsToCurrentFilter = useCallback(
    (product: ProductData): boolean => {
      if (matchType === 'matched') {
        return true;
      }
      if (matchType === 'machineUnmatched') {
        return false;
      }
      const hasHumanMatch = (product.humanMatchedProducts || []).some(
        (competitor) => competitor.competitorProducts.length > 0,
      );
      return !hasHumanMatch && !product.humanVerifiedNoCompetitor;
    },
    [matchType],
  );

  const refreshProductData = useCallback(
    async (sku: string) => {
      const freshData = await competitorClient.getMatchedProductsBySkus([sku]);
      const mp = freshData?.[0];
      if (mp?.skinSeoulProduct) {
        const sp = mp.skinSeoulProduct as Record<string, unknown>;
        const verifiedNoCompetitor = (mp as unknown as { humanVerifiedNoCompetitor?: boolean }).humanVerifiedNoCompetitor;
        const updated: ProductData = {
          skinSeoulProduct: { ...mp.skinSeoulProduct, size: (sp['size'] as string) || '', pack: (sp['pack'] as string) || '' },
          machineMatchedProducts: mp.machineMatchedProducts || [],
          humanMatchedProducts: mp.humanMatchedProducts || [],
          ...(verifiedNoCompetitor === undefined ? {} : { humanVerifiedNoCompetitor: verifiedNoCompetitor }),
          ...(mp.humanDecidedBy === undefined ? {} : { humanDecidedBy: mp.humanDecidedBy }),
          ...(mp.humanDecidedAt === undefined ? {} : { humanDecidedAt: mp.humanDecidedAt }),
        };
        setSelectedProduct((prev) => (prev?.skinSeoulProduct.sku === sku ? updated : prev));
        setProducts((prev) => {
          if (!belongsToCurrentFilter(updated)) {
            return prev.filter((p) => p.skinSeoulProduct.sku !== sku);
          }
          return prev.map((p) => (p.skinSeoulProduct.sku === sku ? updated : p));
        });
      }
      queryClient.invalidateQueries({ queryKey: ['matched-products-by-skus'] });
      queryClient.invalidateQueries({ queryKey: ['list-matched-products'] });
      queryClient.invalidateQueries({ queryKey: ['human-unmatched-products'] });
      queryClient.invalidateQueries({ queryKey: ['machine-unmatched-products'] });
    },
    [competitorClient, queryClient, belongsToCurrentFilter],
  );

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setLeftWidth(Math.min(Math.max(pct, 30), 70));
    },
    [isResizing],
  );

  const handleMouseUp = useCallback(() => setIsResizing(false), []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    const searchParam = searchParams.get('search');
    if (searchParam) {
      setSearchQuery(searchParam);
      setAppliedSearchQuery(searchParam);
      setIsSearchEnabled(true);
    }
  }, []);

  const renderCompetitorEditor = () => {
    if (!selectedProduct) {
      return (
        <div className="text-center text-muted py-5">
          <GitCompare size={48} className="mb-3 opacity-50" />
          <h5>Select a Product</h5>
          <p>Choose a SkinSeoul product from the table to manage competitor matches.</p>
        </div>
      );
    }

    return (
      <CompetitorSelectionSection
        selectedProduct={selectedProduct}
        activeCompetitor={activeCompetitor}
        selectedProducts={selectedProducts}
        handleCompetitorProductSelect={handleCompetitorProductSelect}
        isProductSelected={isProductSelected}
        setActiveCompetitor={setActiveCompetitor}
        competitorClient={competitorClient}
        onProductDataUpdate={updateSelectedProduct}
        onRefreshProductData={refreshProductData}
        actionedBy={reviewerEmail}
      />
    );
  };

  return (
    <div style={{ height: 'calc(100vh - 70px)', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '8px 0' }}>
      <div className="mb-2 d-flex align-items-center" style={{ flexShrink: 0 }}>
        <GitCompare size={20} className="me-2" />
        <h4 className="mb-0">Competitor Match</h4>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-2" style={{ flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      <div
        ref={containerRef}
        style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}
      >
        <div style={{ width: `${leftWidth}%`, minWidth: '400px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Card className="h-100 d-flex flex-column">
            <Card.Header style={{ backgroundColor: '#000', color: '#fff', position: 'relative', zIndex: 10, flexShrink: 0 }}>
              <ProductTableHeader
                productCount={filteredProducts.length}
                pagination={pagination}
                onPageSizeChange={handlePageSizeChange}
                expandedCount={expandedProducts.size}
                onCollapseAll={() => setExpandedProducts(new Set())}
                sort={sort}
                onSortChange={handleSortChange}
              />
              <CompetitorProductFilters
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
                matchType={matchType}
                onMatchTypeChange={handleMatchTypeChange}
                alertFilter={alertFilter}
                onAlertFilterChange={setAlertFilter}
                candidateFilter={candidateFilter}
                onCandidateFilterChange={handleCandidateFilterChange}
                skuLastDigit={skuLastDigit}
                onSkuLastDigitChange={handleSkuLastDigitChange}
                actionedBy={actionedByFilter}
                onActionedByChange={handleActionedByChange}
                reviewers={knownReviewers}
                onSearch={executeSearch}
                onClear={clearFilters}
                isLoading={isLoading}
              />
            </Card.Header>

            <Card.Body className="p-1" style={{ flex: 1, overflowY: 'auto', position: 'relative', minHeight: 0 }}>
              {isLoading && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                  }}
                >
                  <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem', marginBottom: '1rem' }}>
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <h5 className="mb-1">Loading Products...</h5>
                </div>
              )}

              <div className="p-1">
                {filteredProducts.length === 0 && !isLoading ? (
                  <div className="text-center py-5">
                    <Package size={48} className="text-muted mb-3" />
                    <h5>No Products Found</h5>
                    <p className="text-muted">
                      {products.length > 0 && alertFilter !== 'all'
                        ? 'No products match the current alert filter'
                        : 'Click Search to load products or adjust your filters'}
                    </p>
                  </div>
                ) : (
                  displayedProducts.map((product, index) => {
                    const sku = product.skinSeoulProduct.sku;
                    const isExpanded = expandedProducts.has(sku);
                    const hasVariations =
                      product.skinSeoulProduct.type === 'Variable' &&
                      product.skinSeoulProduct.variations &&
                      product.skinSeoulProduct.variations.length > 0;

                    return (
                      <React.Fragment key={sku}>
                        <ProductCard
                          product={product}
                          index={(displayPage - 1) * itemsPerPage + index}
                          isSelected={selectedProduct?.skinSeoulProduct.sku === sku}
                          competitorCount={countCompetitors(product)}
                          onSelect={() => handleProductSelect(product)}
                          isExpanded={isExpanded}
                          onToggleExpand={() => toggleProductExpansion(sku)}
                        />

                        {hasVariations &&
                          isExpanded &&
                          product.skinSeoulProduct.variations!.map((variation, varIndex) => (
                            <ProductCard
                              key={`${sku}-var-${variation.sku}`}
                              product={{
                                skinSeoulProduct: variation,
                                machineMatchedProducts: [],
                                humanMatchedProducts: [],
                              }}
                              index={varIndex}
                              isSelected={selectedProduct?.skinSeoulProduct.sku === variation.sku}
                              competitorCount={0}
                              onSelect={() =>
                                handleProductSelect({
                                  skinSeoulProduct: variation,
                                  machineMatchedProducts: product.machineMatchedProducts,
                                  humanMatchedProducts: product.humanMatchedProducts || [],
                                })
                              }
                              isVariation
                            />
                          ))}
                      </React.Fragment>
                    );
                  })
                )}
              </div>
            </Card.Body>

            {filteredProducts.length > 0 && (
              <Card.Footer style={{ backgroundColor: '#1a1a1a', borderTop: '1px solid #333', padding: '8px 16px', flexShrink: 0 }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2">
                    <small style={{ color: '#ccc' }}>
                      {(displayPage - 1) * itemsPerPage + 1}-{Math.min(displayPage * itemsPerPage, filteredProducts.length)} of {filteredProducts.length}
                      {alertFilter !== 'all' && ` (filtered from ${products.length})`}
                      {isBackgroundLoading && ' (loading...)'}
                      {isPaginationStopped && ' (stopped)'}
                    </small>
                    {(isBackgroundLoading || isPaginationStopped) && (
                      <Button
                        size="sm"
                        variant={isPaginationStopped ? 'success' : 'warning'}
                        onClick={() => setIsPaginationStopped(!isPaginationStopped)}
                        style={{ padding: '2px 8px' }}
                        title={isPaginationStopped ? 'Resume loading' : 'Stop loading'}
                      >
                        {isPaginationStopped ? <Play size={14} /> : <Pause size={14} />}
                      </Button>
                    )}
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    {isBackgroundLoading && <Spinner animation="border" size="sm" variant="primary" />}

                    <ButtonGroup size="sm">
                      <Button variant="outline-secondary" onClick={() => goToPage(displayPage - 1)} disabled={displayPage <= 1}>
                        <ChevronLeft size={14} />
                      </Button>
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (displayPage <= 3) {
                          pageNum = i + 1;
                        } else if (displayPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = displayPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={displayPage === pageNum ? 'primary' : 'outline-secondary'}
                            onClick={() => goToPage(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                      <Button variant="outline-secondary" onClick={() => goToPage(displayPage + 1)} disabled={displayPage >= totalPages}>
                        <ChevronRight size={14} />
                      </Button>
                    </ButtonGroup>
                  </div>
                </div>
              </Card.Footer>
            )}
          </Card>
        </div>

        <div
          onMouseDown={handleMouseDown}
          style={{
            width: '8px',
            backgroundColor: isResizing ? '#007bff' : '#dee2e6',
            cursor: 'col-resize',
            flexShrink: 0,
            borderLeft: '1px solid #ccc',
            borderRight: '1px solid #ccc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: isResizing ? 'none' : 'background-color 0.2s',
          }}
        >
          <div style={{ width: '2px', height: '40px', backgroundColor: '#6c757d', borderRadius: '1px' }} />
        </div>

        <div style={{ width: `${100 - leftWidth}%`, minWidth: '400px', display: 'flex', flexDirection: 'column' }}>
          <Card className="h-100">
            <Card.Header style={{ backgroundColor: '#000', color: '#fff', padding: '12px 16px' }}>
              <h6 className="mb-0">Competitor Editor</h6>
            </Card.Header>
            <Card.Body style={{ overflowY: 'auto', padding: '16px' }}>{renderCompetitorEditor()}</Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
}
